import { lazy, ComponentType } from "react";

const RETRY_KEY = "chunk-retry-refreshed";

function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return (
      msg.includes("loading chunk") ||
      msg.includes("loading css chunk") ||
      msg.includes("dynamically imported module") ||
      msg.includes("failed to fetch") ||
      msg.includes("importing a module script failed") ||
      error.name === "ChunkLoadError"
    );
  }
  return false;
}

function hardReload() {
  // Bust any HTML/asset cache so we get the freshest index.html + chunk map
  const url = new URL(window.location.href);
  url.searchParams.set("_r", Date.now().toString(36));
  window.location.replace(url.toString());
}

/**
 * Wraps React.lazy with automatic recovery on chunk load failures.
 *
 * After a new deploy, the previously loaded index.html points at chunk URLs
 * that no longer exist (404 -> "Failed to fetch dynamically imported module").
 * Strategy:
 *   1. Retry the import once with a cache-busting query (fixes transient/cached
 *      network failures without a full page reload).
 *   2. If that fails too, do a one-time cache-busting page reload to pick up
 *      the new asset manifest.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
) {
  const load = async (): Promise<{ default: T }> => {
    const mod = await importFn();
    // Guard against a resolved-but-empty module (stale/partial chunk), which
    // otherwise crashes React with "Cannot read properties of undefined
    // (reading 'default')" and a blank screen.
    if (!mod || !(mod as { default?: T }).default) {
      throw new Error("Loading chunk failed: module has no default export");
    }
    return mod;
  };

  return lazy(() =>
    load().catch(async (error) => {
      if (!isChunkLoadError(error)) throw error;

      // Attempt an immediate in-place retry (network hiccups / stale cache).
      try {
        return await load();
      } catch (retryError) {
        if (!isChunkLoadError(retryError)) throw retryError;
      }

      let hasRefreshed: string | null = null;
      try {
        hasRefreshed = sessionStorage.getItem(RETRY_KEY);
      } catch {
        /* storage unavailable (sandboxed iframe) */
      }

      if (!hasRefreshed) {
        try {
          sessionStorage.setItem(RETRY_KEY, "true");
        } catch {
          /* ignore */
        }
        hardReload();
        // Never-resolving promise so React doesn't render an error state
        // while the reload is in flight.
        return new Promise<{ default: T }>(() => {});
      }

      // Already reloaded once this session — clear so a future deploy can retry.
      try {
        sessionStorage.removeItem(RETRY_KEY);
      } catch {
        /* ignore */
      }
      throw error;
    }),
  );
}


/**
 * Clear the retry flag once the app has clearly loaded successfully, so future
 * deploys can trigger another recovery. Delayed slightly so that a reload which
 * immediately fails again is still detected as a repeat (prevents reload loops).
 */
export function clearRetryFlag() {
  setTimeout(() => {
    try {
      sessionStorage.removeItem(RETRY_KEY);
    } catch {
      /* ignore */
    }
  }, 10000);
}
